import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { ChevronRight } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { z } from 'zod'
import isEqual from 'lodash/isEqual'

import Button from '@/components-latest/primitives/Button'
import Text from '@/components-latest/primitives/Text'
import { Form } from '@/components-latest/shared/Form'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import EditMenuItemDetails from '@/components-latest/ui/MenuPage/EditMenuItemModal/EditMenuItemDetails'
import {
  getAddonsOrPacksErrorBoolean,
  mapMenuItemToEditModal
} from '@/components-latest/ui/MenuPage/EditMenuItemModal/helpers'
import { IError } from '@/config/http/http.helpers'
import { useEditMenuItemMutation } from '@/ducks/menu/api/menu.api'
import { mapEditMenuItemDTO } from '@/ducks/menu/helpers/menu.helpers'
import { IMenuItem } from '@/ducks/menu/menu.types'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import { pushGtmFormSubmitEvent } from '@/utils/gtm.utils'

import EditMenuItemAddons from './EditMenuItemAddons'
import Styles from './EditMenuItemModal.module.scss'

const addonsSchema = z.record(
  z.object({
    visibility: z.union([z.literal(0), z.literal(1)]),
    price: z.number().min(0)
  })
)
export type IAddonsTypeInForm = z.infer<typeof addonsSchema>

const packSchema = z.record(
  z.object({
    visibility: z.union([z.literal(0), z.literal(1)]),
    price: z.number().min(0),
    categoryId: z.string().min(1)
  })
)
export type IPackTypeInForm = z.infer<typeof packSchema>

export const editMenuItemformSchema = z.object({
  food_id: z.string(),
  food_name: z.string().min(1, { message: 'Required' }),
  price: z.number({ required_error: 'Required' }).min(0),
  maxQuantity: z.number({ required_error: 'Required' }).min(0),
  addons: addonsSchema,
  base_pack: packSchema,
  type_pack: packSchema,
  _modalPrice: z.number()
})
export type IEditMenuItemFormData = z.infer<typeof editMenuItemformSchema>

const formDefaultValues = {
  food_id: '',
  food_name: '',
  price: 0,
  maxQuantity: 0,
  addons: {},
  type_pack: {},
  base_pack: {},
  _modalPrice: 0
} as IEditMenuItemFormData

export const EDIT_MENU_ITEM_CACHE_KEY = 'EDIT_MENU_ITEM'

type Props = {
  isOpen: boolean
  closeModal: () => void
  menuItem: IMenuItem | null
  storeId?: string | null
}

function getChangedFields(
  original: IEditMenuItemFormData,
  updated: IEditMenuItemFormData
): Partial<IEditMenuItemFormData> {
  const changed: Partial<IEditMenuItemFormData> = {}

  for (const key of Object.keys(updated) as (keyof IEditMenuItemFormData)[]) {
    const originalValue = original[key]
    const updatedValue = updated[key]

    if (!isEqual(originalValue, updatedValue)) {
      ;(changed as Record<string, unknown>)[key] = updatedValue
    }
  }

  return changed as Partial<IEditMenuItemFormData>
}

const EditMenuItemModal = React.memo(
  ({ menuItem, isOpen, closeModal, storeId }: Props) => {
    const { t } = useTranslation()

    const form = useForm<IEditMenuItemFormData>({
      resolver: zodResolver(editMenuItemformSchema),
      mode: 'onSubmit',
      defaultValues: formDefaultValues,
      reValidateMode: 'onSubmit',
      shouldFocusError: false
    })

    const {
      handleSubmit,
      clearErrors,
      reset,
      formState: { errors }
    } = form

    const [areAddonsShown, setAreAddonsShown] = useState(false)
    const openAddons = () => {
      setAreAddonsShown(true)
    }
    const closeAddons = useCallback(() => {
      setAreAddonsShown(false)
    }, [])

    const enhancedCloseModal = useCallback(() => {
      closeModal()
      closeAddons()
      clearErrors()
      reset(formDefaultValues)
    }, [clearErrors, closeModal, reset, closeAddons])

    useEffect(() => {
      if (isOpen && (!menuItem || !menuItem?._id)) enhancedCloseModal()
    }, [isOpen, menuItem, enhancedCloseModal])

    useEffect(() => {
      if (menuItem) {
        reset(mapMenuItemToEditModal(menuItem))
      }
    }, [menuItem, reset])

    const dispatch = useDispatch()
    const [editMenuItem, { isLoading }] = useEditMenuItemMutation({
      fixedCacheKey: EDIT_MENU_ITEM_CACHE_KEY
    })

    const onSubmit = useCallback(
      (values: IEditMenuItemFormData) => {
        if (!menuItem) return

        pushGtmFormSubmitEvent()

        const original = mapMenuItemToEditModal(menuItem)
        const changedFields = getChangedFields(original, values)
        const payload = {
          ...mapEditMenuItemDTO(changedFields),
          store_id: storeId ?? undefined,
          food_id: original.food_id
        }
        editMenuItem(payload)
          .unwrap()
          .then(() => {
            dispatch(
              addSystemNotification({
                message: t('menu.edit-modal.success'),
                intent: 'success'
              })
            )
            enhancedCloseModal()
          })
          .catch((error: IError) => {
            dispatch(
              addSystemNotification({
                message: error.message,
                intent: 'error'
              })
            )
            enhancedCloseModal()
          })
      },
      [editMenuItem, dispatch, enhancedCloseModal, t, storeId, menuItem]
    )

    const arePacksHavingError = getAddonsOrPacksErrorBoolean(errors)

    const hasAddonsOrPacks = useMemo(() => {
      return (
        !!menuItem?.addons?.length ||
        !!menuItem?.type_pack?.length ||
        !!menuItem?.base_pack?.length
      )
    }, [menuItem])

    return (
      <CommonModal
        className={Styles.modal}
        title={
          areAddonsShown
            ? t('menu.edit-modal.addons-title')
            : t('menu.edit-modal.title')
        }
        open={isOpen}
        onGoBack={areAddonsShown ? closeAddons : undefined}
        onOpenChange={enhancedCloseModal}
        body={
          <div>
            <Form {...form}>
              <form className={Styles.form} onSubmit={handleSubmit(onSubmit)}>
                {!areAddonsShown ? (
                  <>
                    <EditMenuItemDetails isLoading={isLoading} form={form} />
                    <button
                      disabled={!hasAddonsOrPacks}
                      onClick={openAddons}
                      className={clsx(Styles.addonsButton, {
                        [Styles.faded]: !hasAddonsOrPacks,
                        [Styles.error]: arePacksHavingError
                      })}>
                      <Text variant="nextSubtitle" weight="bold" asChild>
                        <p>{t('menu.edit-modal.addons-link')}</p>
                      </Text>
                      <ChevronRight />
                    </button>
                  </>
                ) : (
                  <EditMenuItemAddons
                    menuItem={menuItem}
                    form={form}
                    isLoading={isLoading}
                  />
                )}
                <Button
                  className={Styles.submitButton}
                  disabled={isLoading}
                  loading={isLoading}
                  variant="primary"
                  type="submit">
                  <p>{t('menu.edit-modal.validate')}</p>
                </Button>
              </form>
            </Form>
          </div>
        }
      />
    )
  }
)
EditMenuItemModal.displayName = 'EditMenuItemModal'

export default EditMenuItemModal
